// HM-304: wrapper SortableList per riordino verticale di items.
// Usato in cluster 24 (riordino menu room service) e altri punti drag&drop.
// Per use-case piu' complessi (planning Gantt, kanban, KNX automazioni)
// importare direttamente DndContext da '@dnd-kit/core'.

import { DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ReactNode } from 'react';

export interface SortableItem {
  id: string;
}

export interface SortableListProps<T extends SortableItem> {
  items: T[];
  onReorder: (next: T[]) => void;
  renderItem: (item: T, isDragging: boolean) => ReactNode;
  className?: string;
}

export function SortableList<T extends SortableItem>(props: SortableListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = props.items.findIndex((it) => it.id === active.id);
    const newIndex = props.items.findIndex((it) => it.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    props.onReorder(arrayMove(props.items, oldIndex, newIndex));
  }

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <SortableContext
        items={props.items.map((it) => it.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className={props.className}>
          {props.items.map((item) => (
            <SortableRow key={item.id} id={item.id}>
              {(isDragging) => props.renderItem(item, isDragging)}
            </SortableRow>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

interface SortableRowProps {
  id: string;
  children: (isDragging: boolean) => ReactNode;
}

function SortableRow({ id, children }: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children(isDragging)}
    </div>
  );
}
